/**
 * Especialidades de buceo (PADI/SSI)
 * Cada especialidad es un mini-curso con sus propios objetivos
 * Disponibles como módulos independientes tras completar Open Water
 */
import type { CourseObjective, CourseModule, Course } from './openWater'

// ─── Tipos de especialidad ──────────────────────────────────────
export interface DivingSpecialty {
  id: string
  name: string
  nameEn: string
  description: string
  descriptionEn: string
  emoji: string
  color: string
  gradient: string
  /** Query para búsqueda de imágenes en MarketIQ */
  imageQuery: string
  imageQueryEn: string
  /** Nivel de dificultad 1-5 */
  difficulty: number
  /** Inmersiones requeridas */
  requiredDives: number
  /** Prerrequisito de certificación */
  prerequisite: 'open-water' | 'advanced-open-water' | 'rescue-diver'
  /** Profundidad máxima de la especialidad */
  maxDepth?: number
  /** Tags para filtrado */
  tags: string[]
  /** Datos clave / highlights */
  highlights: { emoji: string; text: string; textEn: string }[]
}

// ─── Catálogo de 16 especialidades ──────────────────────────────
export const divingSpecialties: DivingSpecialty[] = [
  // 1. Peak Performance Buoyancy
  {
    id: 'spec-buoyancy',
    name: 'Flotabilidad Perfecta',
    nameEn: 'Peak Performance Buoyancy',
    description: 'Domina el control total de tu flotabilidad. Muévete como un pez.',
    descriptionEn: 'Master total buoyancy control. Move like a fish.',
    emoji: '⚖️',
    color: 'cyan',
    gradient: 'from-cyan-400 to-teal-500',
    imageQuery: 'buceador flotabilidad perfecta neutra submarino',
    imageQueryEn: 'scuba diver perfect buoyancy neutral underwater',
    difficulty: 2,
    requiredDives: 2,
    prerequisite: 'open-water',
    tags: ['fundamental', 'habilidad', 'principiante'],
    highlights: [
      { emoji: '🧘', text: 'Flotabilidad neutra estática y dinámica', textEn: 'Static and dynamic neutral buoyancy' },
      { emoji: '🎯', text: 'Configuración de lastre perfecto', textEn: 'Perfect weight configuration' },
      { emoji: '🐟', text: 'Técnicas de respiración para micro-ajustes', textEn: 'Breathing techniques for micro-adjustments' },
      { emoji: '🌿', text: 'Mínimo impacto ambiental', textEn: 'Minimal environmental impact' },
    ],
  },
  // 2. Deep Diving
  {
    id: 'spec-deep',
    name: 'Buceo Profundo',
    nameEn: 'Deep Diving',
    description: 'Explora las profundidades hasta 40m. Gases, narcosis y planificación avanzada.',
    descriptionEn: 'Explore depths to 40m. Gases, narcosis and advanced planning.',
    emoji: '🏊',
    color: 'indigo',
    gradient: 'from-indigo-500 to-blue-700',
    imageQuery: 'buceo profundo abismo submarino blue hole',
    imageQueryEn: 'deep diving abyss underwater blue hole',
    difficulty: 4,
    requiredDives: 4,
    prerequisite: 'advanced-open-water',
    maxDepth: 40,
    tags: ['avanzado', 'profundo', 'técnico'],
    highlights: [
      { emoji: '📏', text: 'Inmersiones hasta 40m / 130ft', textEn: 'Dives to 40m / 130ft' },
      { emoji: '🧪', text: 'Leyes de gases aplicadas', textEn: 'Applied gas laws' },
      { emoji: '🥴', text: 'Reconocer y manejar narcosis', textEn: 'Recognize and manage narcosis' },
      { emoji: '💻', text: 'Planificación con ordenador de buceo', textEn: 'Dive computer planning' },
    ],
  },
  // 3. Enriched Air Nitrox
  {
    id: 'spec-nitrox',
    name: 'Nitrox (Aire Enriquecido)',
    nameEn: 'Enriched Air Nitrox',
    description: 'Buceo con mezclas de oxígeno hasta 40%. Más tiempo de fondo.',
    descriptionEn: 'Diving with oxygen mixes up to 40%. More bottom time.',
    emoji: '🫧',
    color: 'green',
    gradient: 'from-green-400 to-emerald-600',
    imageQuery: 'nitrox tanque buceo aire enriquecido analisis gas',
    imageQueryEn: 'nitrox tank scuba diving enriched air gas analysis',
    difficulty: 2,
    requiredDives: 2,
    prerequisite: 'open-water',
    tags: ['gas', 'fundamental', 'técnico'],
    highlights: [
      { emoji: '⏱️', text: 'Más tiempo de fondo vs. aire normal', textEn: 'More bottom time vs. normal air' },
      { emoji: '🔬', text: 'Análisis de mezcla de gas', textEn: 'Gas mix analysis' },
      { emoji: '📊', text: 'Cálculo de MOD y PPO₂', textEn: 'MOD and PPO₂ calculation' },
      { emoji: '⚠️', text: 'Limites de toxicidad de O₂', textEn: 'O₂ toxicity limits' },
    ],
  },
  // 4. Night Diving
  {
    id: 'spec-night',
    name: 'Buceo Nocturno',
    nameEn: 'Night Diving',
    description: 'Descubre el arrecife de noche. Criaturas nocturnas y bioluminiscencia.',
    descriptionEn: 'Discover the reef at night. Nocturnal creatures and bioluminescence.',
    emoji: '🌙',
    color: 'purple',
    gradient: 'from-purple-500 to-indigo-700',
    imageQuery: 'buceo nocturno arrecife coral noche linterna submarino',
    imageQueryEn: 'night diving reef coral underwater torch flashlight',
    difficulty: 3,
    requiredDives: 3,
    prerequisite: 'open-water',
    tags: ['experiencia', 'nocturno', 'vida marina'],
    highlights: [
      { emoji: '🔦', text: 'Técnicas de iluminación y señalización', textEn: 'Lighting and signaling techniques' },
      { emoji: '🦑', text: 'Vida marina nocturna: pulpos, langostas, morenas', textEn: 'Nocturnal marine life: octopi, lobsters, moray eels' },
      { emoji: '🌟', text: 'Bioluminiscencia y plancton luminoso', textEn: 'Bioluminescence and luminous plankton' },
      { emoji: '🤝', text: 'Comunicación con linterna', textEn: 'Flashlight communication' },
    ],
  },
  // 5. Underwater Navigation
  {
    id: 'spec-navigation',
    name: 'Navegación Submarina',
    nameEn: 'Underwater Navigation',
    description: 'Domina brújula y navegación natural. Nunca pierdas tu rumbo.',
    descriptionEn: 'Master compass and natural navigation. Never lose your way.',
    emoji: '🧭',
    color: 'amber',
    gradient: 'from-amber-400 to-orange-500',
    imageQuery: 'navegacion submarina brujula buceo orientacion',
    imageQueryEn: 'underwater navigation compass scuba diving orientation',
    difficulty: 2,
    requiredDives: 3,
    prerequisite: 'open-water',
    tags: ['fundamental', 'habilidad', 'navegación'],
    highlights: [
      { emoji: '🧲', text: 'Uso de brújula submarina', textEn: 'Underwater compass use' },
      { emoji: '🌊', text: 'Navegación natural (sol, corrientes, fondo)', textEn: 'Natural navigation (sun, currents, bottom)' },
      { emoji: '📐', text: 'Patrones: cuadrado, triangular, espiral', textEn: 'Patterns: square, triangular, spiral' },
      { emoji: '📏', text: 'Cálculo de distancias por ciclos de aleteo', textEn: 'Distance calculation by kick cycles' },
    ],
  },
  // 6. Wreck Diving
  {
    id: 'spec-wreck',
    name: 'Buceo en Pecios',
    nameEn: 'Wreck Diving',
    description: 'Explora barcos hundidos y restos sumergidos. Historia bajo el agua.',
    descriptionEn: 'Explore sunken ships and submerged wrecks. History underwater.',
    emoji: '🚢',
    color: 'slate',
    gradient: 'from-slate-500 to-gray-700',
    imageQuery: 'buceo pecio barco hundido naufragio submarino',
    imageQueryEn: 'wreck diving sunken ship underwater shipwreck',
    difficulty: 3,
    requiredDives: 4,
    prerequisite: 'advanced-open-water',
    tags: ['exploración', 'aventura', 'historia'],
    highlights: [
      { emoji: '🏚️', text: 'Exploración exterior e interior de pecios', textEn: 'External and internal wreck exploration' },
      { emoji: '🧵', text: 'Uso de carrete y línea guía', textEn: 'Reel and guideline use' },
      { emoji: '⚠️', text: 'Peligros: sedimento, cables, espacios cerrados', textEn: 'Hazards: silt, cables, confined spaces' },
      { emoji: '📜', text: 'Historia y conservación de pecios', textEn: 'Wreck history and conservation' },
    ],
  },
  // 7. Underwater Photography
  {
    id: 'spec-photo',
    name: 'Fotografía Submarina',
    nameEn: 'Underwater Photography',
    description: 'Captura la belleza del océano. Técnicas de composición y luz.',
    descriptionEn: 'Capture the beauty of the ocean. Composition and light techniques.',
    emoji: '📸',
    color: 'pink',
    gradient: 'from-pink-400 to-rose-600',
    imageQuery: 'fotografia submarina camara buceo arrecife coral',
    imageQueryEn: 'underwater photography camera scuba diving reef coral',
    difficulty: 2,
    requiredDives: 2,
    prerequisite: 'open-water',
    tags: ['creatividad', 'foto', 'vida marina'],
    highlights: [
      { emoji: '📷', text: 'Equipos: carcasas, estrobos, filtros', textEn: 'Equipment: housings, strobes, filters' },
      { emoji: '🎨', text: 'Composición y regla de tercios', textEn: 'Composition and rule of thirds' },
      { emoji: '💡', text: 'Iluminación artificial vs. natural', textEn: 'Artificial vs. natural lighting' },
      { emoji: '🐠', text: 'Macro, gran angular y close-up', textEn: 'Macro, wide angle and close-up' },
    ],
  },
  // 8. Fish Identification
  {
    id: 'spec-fish-id',
    name: 'Identificación de Peces',
    nameEn: 'Fish Identification',
    description: 'Reconoce las especies marinas. De los corales a los pelágicos.',
    descriptionEn: 'Recognize marine species. From corals to pelagics.',
    emoji: '🐠',
    color: 'teal',
    gradient: 'from-teal-400 to-cyan-600',
    imageQuery: 'identificacion peces marinos arrecife especies tropicales',
    imageQueryEn: 'fish identification marine reef species tropical',
    difficulty: 1,
    requiredDives: 2,
    prerequisite: 'open-water',
    tags: ['biología', 'vida marina', 'ecología'],
    highlights: [
      { emoji: '🔍', text: 'Familias de peces: ángel, mariposa, loro, cirujano...', textEn: 'Fish families: angel, butterfly, parrot, surgeon...' },
      { emoji: '🌊', text: 'Hábitats: arrecife, pelágico, fondo arenoso', textEn: 'Habitats: reef, pelagic, sandy bottom' },
      { emoji: '🦈', text: 'Comportamiento y patrones de alimentación', textEn: 'Behavior and feeding patterns' },
      { emoji: '📋', text: 'Registro de avistamientos (fish log)', textEn: 'Sighting record (fish log)' },
    ],
  },
  // 9. Underwater Naturalist
  {
    id: 'spec-naturalist',
    name: 'Naturalista Submarino',
    nameEn: 'Underwater Naturalist',
    description: 'Comprende el ecosistema marino completo. Interacciones y conservación.',
    descriptionEn: 'Understand the complete marine ecosystem. Interactions and conservation.',
    emoji: '🐚',
    color: 'emerald',
    gradient: 'from-emerald-400 to-green-600',
    imageQuery: 'ecosistema marino coral arrecife biodiversidad submarina',
    imageQueryEn: 'marine ecosystem coral reef biodiversity underwater',
    difficulty: 1,
    requiredDives: 2,
    prerequisite: 'open-water',
    tags: ['ecología', 'conservación', 'biología'],
    highlights: [
      { emoji: '🪸', text: 'Ecosistemas: arrecifes, manglares, praderas', textEn: 'Ecosystems: reefs, mangroves, seagrass' },
      { emoji: '🔗', text: 'Cadenas alimentarias marinas', textEn: 'Marine food chains' },
      { emoji: '🐙', text: 'Invertebrados: esponjas, medusas, moluscos', textEn: 'Invertebrates: sponges, jellies, mollusks' },
      { emoji: '♻️', text: 'Impacto humano y conservación', textEn: 'Human impact and conservation' },
    ],
  },
  // 10. Search & Recovery
  {
    id: 'spec-search-recovery',
    name: 'Búsqueda y Recuperación',
    nameEn: 'Search & Recovery',
    description: 'Técnicas de rastreo y recuperación de objetos sumergidos.',
    descriptionEn: 'Techniques for tracking and recovering submerged objects.',
    emoji: '🔍',
    color: 'orange',
    gradient: 'from-orange-400 to-red-500',
    imageQuery: 'busqueda recuperacion objetos submarinos buceo rastreo',
    imageQueryEn: 'search recovery underwater objects diving tracking',
    difficulty: 3,
    requiredDives: 4,
    prerequisite: 'advanced-open-water',
    tags: ['técnico', 'habilidad', 'avanzado'],
    highlights: [
      { emoji: '🔎', text: 'Patrones de búsqueda: circular, paralelo, jack-stay', textEn: 'Search patterns: circular, parallel, jack-stay' },
      { emoji: '🎈', text: 'Uso de bolsas elevadoras (lift bags)', textEn: 'Lift bag use' },
      { emoji: '🪢', text: 'Nudos y cabos de buceo', textEn: 'Diving knots and lines' },
      { emoji: '📋', text: 'Planificación de operaciones de rescate', textEn: 'Rescue operation planning' },
    ],
  },
  // 11. Dry Suit
  {
    id: 'spec-dry-suit',
    name: 'Traje Seco',
    nameEn: 'Dry Suit Diving',
    description: 'Bucea cómodo en aguas frías. Técnicas de manejo del traje seco.',
    descriptionEn: 'Dive comfortably in cold water. Dry suit handling techniques.',
    emoji: '🧊',
    color: 'blue',
    gradient: 'from-blue-400 to-sky-600',
    imageQuery: 'buceo traje seco aguas frias dry suit diving',
    imageQueryEn: 'dry suit diving cold water scuba diver',
    difficulty: 2,
    requiredDives: 2,
    prerequisite: 'open-water',
    tags: ['equipo', 'frío', 'fundamental'],
    highlights: [
      { emoji: '🌡️', text: 'Protección térmica total', textEn: 'Full thermal protection' },
      { emoji: '🎈', text: 'Control de flotabilidad con traje seco', textEn: 'Buoyancy control with dry suit' },
      { emoji: '👔', text: 'Tipos: neopreno, trilaminado, caucho', textEn: 'Types: neoprene, trilaminate, rubber' },
      { emoji: '🔧', text: 'Mantenimiento y reparación', textEn: 'Maintenance and repair' },
    ],
  },
  // 12. Boat Diving
  {
    id: 'spec-boat',
    name: 'Buceo desde Embarcación',
    nameEn: 'Boat Diving',
    description: 'Técnicas de entrada y salida desde diferentes tipos de embarcaciones.',
    descriptionEn: 'Entry and exit techniques from different types of boats.',
    emoji: '⛵',
    color: 'sky',
    gradient: 'from-sky-400 to-blue-500',
    imageQuery: 'buceo desde barco embarcacion entrada agua',
    imageQueryEn: 'boat diving entry water scuba diving vessel',
    difficulty: 2,
    requiredDives: 2,
    prerequisite: 'open-water',
    tags: ['logística', 'barco', 'fundamental'],
    highlights: [
      { emoji: '🚢', text: 'Tipos de embarcaciones de buceo', textEn: 'Types of dive boats' },
      { emoji: '🏴', text: 'Banderas y señalización marítima', textEn: 'Maritime flags and signaling' },
      { emoji: '🚶', text: 'Técnicas de entrada: gigante, caída, escalera', textEn: 'Entry techniques: giant stride, backward roll, ladder' },
      { emoji: '⚓', text: 'Procedimientos de seguridad a bordo', textEn: 'Onboard safety procedures' },
    ],
  },
  // 13. Drift Diving
  {
    id: 'spec-drift',
    name: 'Buceo en Corriente',
    nameEn: 'Drift Diving',
    description: 'Déjate llevar por la corriente. Técnicas de drift y señalización.',
    descriptionEn: 'Let the current carry you. Drift techniques and signaling.',
    emoji: '🌀',
    color: 'violet',
    gradient: 'from-violet-400 to-purple-600',
    imageQuery: 'drift diving corriente submarina buceadores grupo',
    imageQueryEn: 'drift diving current underwater divers group',
    difficulty: 3,
    requiredDives: 2,
    prerequisite: 'advanced-open-water',
    tags: ['corriente', 'aventura', 'avanzado'],
    highlights: [
      { emoji: '💨', text: 'Técnicas de entrada y descenso en corriente', textEn: 'Current entry and descent techniques' },
      { emoji: '🎈', text: 'Uso de SMB (boya de señalización)', textEn: 'SMB (surface marker buoy) use' },
      { emoji: '🚢', text: 'Live boat y recogida en superficie', textEn: 'Live boat and surface pickup' },
      { emoji: '🌊', text: 'Lectura de corrientes y mareas', textEn: 'Reading currents and tides' },
    ],
  },
  // 14. Rescue Diver
  {
    id: 'spec-rescue',
    name: 'Buceador de Rescate',
    nameEn: 'Rescue Diver',
    description: 'Prevención y manejo de emergencias acuáticas. Salvar vidas.',
    descriptionEn: 'Prevention and management of aquatic emergencies. Save lives.',
    emoji: '🆘',
    color: 'red',
    gradient: 'from-red-500 to-rose-600',
    imageQuery: 'rescate buceo emergencia salvamento submarino',
    imageQueryEn: 'rescue diver scuba emergency underwater lifesaving',
    difficulty: 4,
    requiredDives: 5,
    prerequisite: 'advanced-open-water',
    tags: ['seguridad', 'rescate', 'avanzado'],
    highlights: [
      { emoji: '🩺', text: 'Primeros auxilios y RCP', textEn: 'First aid and CPR' },
      { emoji: '🏊', text: 'Rescate de buceador inconsciente', textEn: 'Unconscious diver rescue' },
      { emoji: '🧠', text: 'Manejo del pánico y estrés', textEn: 'Panic and stress management' },
      { emoji: '📋', text: 'Planes de emergencia', textEn: 'Emergency plans' },
    ],
  },
  // 15. Equipment Specialist
  {
    id: 'spec-equipment',
    name: 'Especialista en Equipo',
    nameEn: 'Equipment Specialist',
    description: 'Conoce a fondo tu equipo. Mantenimiento, configuración y reparación.',
    descriptionEn: 'Know your equipment inside out. Maintenance, configuration and repair.',
    emoji: '🔧',
    color: 'zinc',
    gradient: 'from-zinc-400 to-stone-600',
    imageQuery: 'equipo buceo regulador tanque BCD mantenimiento',
    imageQueryEn: 'scuba equipment regulator tank BCD maintenance',
    difficulty: 2,
    requiredDives: 0,
    prerequisite: 'open-water',
    tags: ['equipo', 'mantenimiento', 'técnico'],
    highlights: [
      { emoji: '🎒', text: 'BCD: tipos, ajustes y mantenimiento', textEn: 'BCD: types, adjustments and maintenance' },
      { emoji: '🫁', text: 'Reguladores: 1ª y 2ª etapa', textEn: 'Regulators: 1st and 2nd stage' },
      { emoji: '💻', text: 'Ordenadores de buceo y consolas', textEn: 'Dive computers and consoles' },
      { emoji: '🔩', text: 'Configuración y revisión periódica', textEn: 'Configuration and periodic review' },
    ],
  },
  // 16. Dive Against Debris (Ecología)
  {
    id: 'spec-ecology',
    name: 'Ecología Marina',
    nameEn: 'Marine Ecology / Dive Against Debris',
    description: 'Limpieza submarina y conservación del océano. Sé parte del cambio.',
    descriptionEn: 'Underwater cleanup and ocean conservation. Be part of the change.',
    emoji: '🌍',
    color: 'lime',
    gradient: 'from-lime-400 to-green-500',
    imageQuery: 'limpieza submarina oceano conservacion marina basura',
    imageQueryEn: 'underwater cleanup ocean conservation marine debris',
    difficulty: 1,
    requiredDives: 1,
    prerequisite: 'open-water',
    tags: ['ecología', 'conservación', 'principiante'],
    highlights: [
      { emoji: '🗑️', text: 'Técnicas de recolección submarina', textEn: 'Underwater collection techniques' },
      { emoji: '📊', text: 'Registro de datos ambientales', textEn: 'Environmental data logging' },
      { emoji: '🐢', text: 'Impacto del plástico en vida marina', textEn: 'Impact of plastic on marine life' },
      { emoji: '🤝', text: 'Iniciativas Project AWARE', textEn: 'Project AWARE initiatives' },
    ],
  },
]

// ─── Helpers ─────────────────────────────────────────────────────
export const getSpecialtyById = (id: string): DivingSpecialty | undefined =>
  divingSpecialties.find(s => s.id === id)

export const getSpecialtiesByPrerequisite = (prereq: string): DivingSpecialty[] =>
  divingSpecialties.filter(s => s.prerequisite === prereq)

export const getSpecialtiesByTag = (tag: string): DivingSpecialty[] =>
  divingSpecialties.filter(s => s.tags.includes(tag))

export const getSpecialtiesByDifficulty = (min: number, max: number): DivingSpecialty[] =>
  divingSpecialties.filter(s => s.difficulty >= min && s.difficulty <= max)

/** Todas las tags únicas */
export const getAllTags = (): string[] =>
  [...new Set(divingSpecialties.flatMap(s => s.tags))].sort()
